import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';

import { Placement } from '@yandex-360/orb-components/dist/components/Popover2';
import { TooltipStateful } from '@yandex-360/orb-components/dist/components/Tooltip';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { BROADCAST_STATUSES_ABLE_TO_END } from '../../../../../../../../common/const/broadcast';
import { Button, ModalConfirm } from '../../../../../../shared/components';
import MoreButtonPopover from '../../../../../../shared/components/more-button-popover';
import { selectIsBroadcastEndEnabled } from '../../../../../../shared/features/config/selectors';
import { closeRoom } from '../../../../../../shared/features/moderation/slice';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { getBroadcastStatus, isSelfModerator } from '../../../../selectors';
import { endBroadcastAction } from '../../../broadcast/slice';
import { ToolbarButton } from '../toolbar-button';
import css from './styles.css';

const METRIKA_NAMESPACE = ['meeting_screen', 'end_call'];

type EndCallButtonProps = {
    onClick: () => void;
    clickMetrika: () => void;
    shouldShowOptions: boolean;
    placement?: Placement;
    disabled?: boolean;
    withAccent?: boolean;
};

export const EndCallButton: FunctionComponent<EndCallButtonProps> = ({
    onClick,
    clickMetrika,
    shouldShowOptions,
    placement = 'top right',
    disabled,
    withAccent,
}) => {
    const dispatch = useDispatch();
    const ref = useRef<HTMLDivElement>(null);

    const [showEndCallPopup, setEndCallPopup] = useState(false);
    const isBroadcastEndEnabled = useSelector(selectIsBroadcastEndEnabled);
    const broadcastStatus = useSelector(getBroadcastStatus);
    const isModerator = useSelector(isSelfModerator);
    const toggleEndCallPopup = useCallback(() => {
        const newValue = !showEndCallPopup;
        if (newValue) {
            countMetrika(...METRIKA_NAMESPACE, 'options');
        }
        setEndCallPopup(newValue);
    }, [showEndCallPopup]);
    const closeEndCallPopup = useCallback(() => {
        setEndCallPopup(false);
    }, []);

    useEffect(() => {
        if (!shouldShowOptions && showEndCallPopup) {
            setEndCallPopup(false);
        }
    }, [shouldShowOptions, showEndCallPopup]);

    const [showModalConfirm, setShowModalConfirm] = useState(false);
    const openModalConfirm = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'confirm_modal', 'open');
        setShowModalConfirm(true);
    }, []);
    const closeModalConfirm = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'confirm_modal', 'close');
        setShowModalConfirm(false);
    }, []);

    const [showCancelModalConfirm, setShowCancelModalConfirm] = useState(false);
    const openCancelModalConfirm = useCallback(() => setShowCancelModalConfirm(true), []);
    const closeCancelModalConfirm = useCallback(() => setShowCancelModalConfirm(false), []);

    const handleClickEndCallForEveryone = useCallback(() => {
        closeEndCallPopup();
        openModalConfirm();
    }, [closeEndCallPopup, openModalConfirm]);

    const handleClickEndCall = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'for_self');
        closeEndCallPopup();
        clickMetrika();
        onClick();
    }, [clickMetrika, closeEndCallPopup, onClick]);

    const endCallForEveryone = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'for_all');
        clickMetrika();
        dispatch(closeRoom());
        setShowModalConfirm(false);
    }, [clickMetrika, dispatch]);

    const onCancelConfirm = useCallback(() => {
        closeCancelModalConfirm();
        dispatch(endBroadcastAction());
        onClick();
    }, [closeCancelModalConfirm, dispatch, onClick]);

    const endCallWithBroadcastForEveryone = useCallback(() => {
        dispatch(endBroadcastAction());
        endCallForEveryone();
    }, [dispatch, endCallForEveryone]);

    const isBroadcastEndAvailable =
        isBroadcastEndEnabled && isModerator && BROADCAST_STATUSES_ABLE_TO_END.has(broadcastStatus);

    const getModalConfirm = () => {
        if (isBroadcastEndAvailable) {
            return (
                <ModalConfirm
                    onConfirm={endCallWithBroadcastForEveryone}
                    onClose={closeModalConfirm}
                    onCancel={closeModalConfirm}
                    headerText={i18n('moderation', 'modal-confirm-end-call-header')}
                    confirmButtonText={i18n('moderation', 'modal-confirm-end-call-button-confirm')}
                    cancelButtonText={i18n('common', 'cancel')}
                >
                    {i18n('moderation', 'modal-confirm-end-call-with-broadcast-content')}
                </ModalConfirm>
            );
        }

        return (
            <ModalConfirm
                onConfirm={endCallForEveryone}
                onClose={closeModalConfirm}
                headerText={i18n('moderation', 'modal-confirm-end-call-header')}
                confirmButtonText={i18n('moderation', 'modal-confirm-end-call-button-confirm')}
                cancelButtonText={i18n('common', 'cancel')}
            >
                {i18n('moderation', 'modal-confirm-end-call-content')}
            </ModalConfirm>
        );
    };

    const handleClick = shouldShowOptions
        ? toggleEndCallPopup
        : isBroadcastEndAvailable
          ? openCancelModalConfirm
          : onClick;

    return (
        <>
            {showModalConfirm ? getModalConfirm() : null}
            {showCancelModalConfirm ? (
                <ModalConfirm
                    onConfirm={onCancelConfirm}
                    onClose={closeCancelModalConfirm}
                    onCancel={closeCancelModalConfirm}
                    headerText={i18n('toolbar', 'quit-call-confirm-modal-header')}
                    confirmButtonText={i18n('toolbar', 'quit-call-confirm-modal-confirm')}
                    cancelButtonText={i18n('common', 'cancel')}
                >
                    {i18n('toolbar', 'quit-call-confirm-modal-content')}
                </ModalConfirm>
            ) : null}
            {shouldShowOptions ? (
                <MoreButtonPopover
                    placement={placement}
                    triggerRef={ref}
                    isOpen={showEndCallPopup}
                    items={[
                        <TooltipStateful
                            key="end-call-for-everyone"
                            content={i18n('toolbar', 'end-call-for-everyone')}
                            trigger="hover"
                        >
                            <div className={cn(css.button, css.accent)}>
                                <Button
                                    view="danger"
                                    size="lg"
                                    width="max"
                                    onClick={handleClickEndCallForEveryone}
                                    testId="end-call-for-everyone-button"
                                >
                                    {i18n('toolbar', 'end-call-for-everyone')}
                                </Button>
                            </div>
                        </TooltipStateful>,
                        <TooltipStateful key="end-call" content={i18n('toolbar', 'end-call')} trigger="hover">
                            <div className={cn(css.button)}>
                                <Button
                                    view="neutral"
                                    size="lg"
                                    width="max"
                                    onClick={handleClickEndCall}
                                    testId="end-call-button"
                                >
                                    {i18n('toolbar', 'end-call')}
                                </Button>
                            </div>
                        </TooltipStateful>,
                    ]}
                    onClose={closeEndCallPopup}
                    className={css.popup}
                />
            ) : null}
            <div ref={ref}>
                <ToolbarButton
                    iconName="callCancel"
                    disabled={disabled}
                    onClick={handleClick}
                    className={cn(css.endCallButton, {
                        [css.withAccent]: withAccent,
                    })}
                    title={i18n('toolbar', 'end-call-alt')}
                    testId="end-call-alt-button"
                    classNameWrapper={css.endCallButtonWrapper}
                />
            </div>
        </>
    );
};
